import { useQuery, UseQueryOptions } from "react-query";
import { TypedJsonResponse } from "../apiClientUtils";
import { Me } from "../authApi";
import { useAuthApiClient } from "../authApi/AuthApiClientContext";
import { useLegoland } from "../LegolandContext";
import { productsFallback, validateProductsFallback } from "./fallbackData";
import { useLegolandApiClient } from "./LegolandApiClientContext";
import {
  LimitSchema,
  MeDetailsFallback,
  MeDetailsSafe,
  MeProductAccess,
  Product,
  ReleaseNotesItem,
} from "./legolandApiTypes";

export interface GetQueryResultOptions {
  parseJson?: boolean;
}

export async function getQueryResult<T>(
  response: TypedJsonResponse<T>,
  options?: GetQueryResultOptions
): Promise<T | null>;
export async function getQueryResult<T>(
  response: Response,
  options?: GetQueryResultOptions
): Promise<T | null>;
export async function getQueryResult<T>(
  response: Response,
  { parseJson = true } = {}
) {
  if (response.ok) {
    return (parseJson ? await response.json() : response.text()) as T;
  } else if (response.status === 401 || response.status === 404) {
    return null;
  }
  throw response;
}

export function useLimitSchemasQuery(
  { productId }: { productId?: string } = {},
  queryOptions?: Omit<
    UseQueryOptions<{
      response: TypedJsonResponse<LimitSchema[]>;
      schemas: LimitSchema[] | null;
    }>,
    "queryKey" | "queryFn"
  >
) {
  const apiClient = useLegolandApiClient();
  return useQuery(
    ["legoland", "limits", "schema", productId],
    async () => {
      const response = await apiClient.getLimitSchemas({
        productId: productId!,
      });
      const schemas = await getQueryResult(response);
      return { schemas, response };
    },
    queryOptions
  );
}

export function useMeDetailsQuery() {
  const legolandApiClient = useLegolandApiClient();
  const authApiClient = useAuthApiClient();
  return useQuery(["legoland", "users", "me", "details"], async () => {
    try {
      const response = await legolandApiClient.getMeDetails();
      return {
        meDetails: await getQueryResult<MeDetailsSafe>(response),
        response,
      };
    } catch (error) {
      const response = await authApiClient.getMe();
      const me = await getQueryResult(response);
      return {
        meDetails: me
          ? (meToMeFallback(legolandApiClient.baseUrl, me) as MeDetailsSafe)
          : null,
        response,
      };
    }
  });
}

export function useMeProductAccessQuery(
  { productId }: { productId?: string } = {},
  queryOptions?: Omit<
    UseQueryOptions<{
      access: MeProductAccess | null;
      response: TypedJsonResponse<MeProductAccess>;
    }>,
    "queryKey" | "queryFn"
  >
) {
  const { productId: currentProductId } = useLegoland();
  const apiClient = useLegolandApiClient();

  productId = productId ?? currentProductId;

  return useQuery(
    ["legoland", "users", "me", "products", productId, "access"],
    async () => {
      const response = await apiClient.getMeProductAccess({
        productId: productId!,
      });
      const access = await getQueryResult(response);
      return { access, response };
    },
    // @ts-ignore
    { enabled: Boolean(productId), ...queryOptions }
  );
}

export function useProductsQuery() {
  const apiClient = useLegolandApiClient();
  return useQuery(["legoland", "products"], async () => {
    const response = await apiClient.getProducts();
    const products = await getQueryResult<Product[]>(response);
    if (products) {
      validateProductsFallback(apiClient.baseUrl, products);
    }
    return { products, response };
  });
}

function meToMeFallback(
  apiUrl: string,
  { access, email, role }: Me
): MeDetailsFallback {
  const products = productsFallback[apiUrl];
  return {
    email,
    isFallback: true,
    products: access.map(({ apiKey, expiryDate, productId }) => {
      const product = products?.find((p) => p.id === productId);
      if (!product) {
        console.error(
          `Fallback data for product with id "${productId}" could not be found.`
        );
      }
      return {
        access: {
          apiKey,
          expiryDate,
          status:
            new Date().setHours(0, 0, 0, 0) > Date.parse(expiryDate)
              ? "EXPIRED"
              : "GRANTED",
        },
        displayName: product?.displayName ?? productId.toUpperCase(),
        id: productId,
        isFallback: true,
        url: product?.redirectLink ?? "#",
      };
    }),
    role,
    settings: {
      clockFormat: "24",
      dateFormat: "DDMMYYYY",
      units: "KM",
      weekStart: "MONDAY",
    },
  };
}

export function useReleaseNotesProductId(productId?: string) {
  const { productId: currentProductId, releaseNotesUrl } = useLegoland();
  return productId ?? currentProductId ?? (releaseNotesUrl ? "ll" : undefined);
}

export function useReleaseNotesQuery(
  { productId }: { productId?: string } = {},
  options?: UseQueryOptions<ReleaseNotesItem[] | null>
) {
  const apiClient = useLegolandApiClient();
  productId = useReleaseNotesProductId(productId);
  return useQuery<ReleaseNotesItem[] | null>(
    ["legoland", "release-notes", productId],
    async () => {
      const response = await apiClient.getReleaseNotes({
        productId: productId!,
      });
      const releaseNotes = (await getQueryResult(response))?.content ?? null;
      return releaseNotes;
    },
    { enabled: Boolean(productId), ...options }
  );
}

export function useUserProduct({ productId }: { productId?: string } = {}) {
  const { productId: currentProductId } = useLegoland();
  const meDetailsQuery = useMeDetailsQuery();
  return meDetailsQuery.data?.meDetails?.products.find(
    (p) => p.id === (productId ?? currentProductId)
  );
}
